import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Dumbbell, Lock, MessageSquare, Smartphone, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { trainingslogAPI } from '@/lib/api';

/* Kraftlog (10.10.2026): Was das Mitglied in der App zu seinen Planer-Einheiten eingetragen hat —
   Plan gegen Ist je Block, Verlauf der Hauptübungen, Coach-Kommentar (sieht das Mitglied in der App). */

const kg = (x) => (x == null ? '–' : `${String(x).replace('.', ',')} kg`);
const datumDe = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');
const sollText = (g) => [g.saetze && g.wdh ? `${g.saetze} × ${g.wdh}` : null, g.intensitaet].filter(Boolean).join(' · ');

/* Zugang zum Kraftlog in der App (10.10.2026). Der Server entscheidet und nennt Grund und Beleg
   (backend/trainingslog/zugang.py, dieselbe Regel wie in der App) — hier wird daraus nur ein Satz
   für den Empfang: „Warum sehe ich den Kraftlog nicht?“ */
const tagKurz = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
const kursMitZeit = (k) => `${k.kurs_name}${k.tag ? ` (${k.tag.slice(0, 2)}${k.start ? ` ${k.start}` : ''})` : ''}`;
const aufzaehlung = (teile, wort) => (teile.length < 2 ? teile.join('') : `${teile.slice(0, -1).join(', ')} ${wort} ${teile[teile.length - 1]}`);
const RECHT_FEHLT = 'das Recht „Trainingshistorie & Gesundheitsdaten einsehen“';

export function belegText(k) {
  if (k.quelle === 'fester_platz') return 'fester Platz';
  if (k.quelle === 'buchung') return `Buchung in der Woche ab ${tagKurz(k.datum)}${k.ausgefallen ? ' (Termin vom Studio abgesagt)' : ''}`;
  return `Check-in${k.datum ? ` am ${tagKurz(k.datum)}` : ''}${k.zur_kurszeit ? ' zur Kurszeit, ohne Buchung' : ' im Kurs'}`;
}

/* Beendete Zyklen, für die die App noch „Nachtragen“ anbietet (bis 14 Tage nach dem Ende) — auch
   wenn sonst nichts läuft. Kommt vom Server (`zugang.nachfrist`), hier wird nur der Satz daraus. */
const nachfristText = (z) => (z.nachfrist || []).map((n) => `${n.name} (bis ${tagKurz(n.frist_bis)})`);

export function zugangText(z, hatEintraege = false) {
  const t = grundText(z, hatEintraege);
  const nach = nachfristText(z);
  if (!nach.length) return t;
  return { ...t, zeilen: [...t.zeilen, `Beendet, Nachtragen in der App geht noch: ${nach.join(', ')}.`] };
}

function grundText(z, hatEintraege) {
  const kurse = z.kurse || [];
  const zyklen = z.zyklen || [];
  const moeglich = (z.zyklus_kurse || []).map(kursMitZeit);
  const nachtragen = nachfristText(z).length > 0;
  // (in der Nachtrag-Frist eines beendeten Zyklus kann das Mitglied sehr wohl noch Einträge anlegen)
  const alt = hatEintraege && !nachtragen ? ['Frühere Einträge bleiben für das Mitglied sichtbar, neue kann es nicht anlegen.'] : [];
  switch (z.grund) {
    case 'team':
      return { ok: true, kopf: 'freigeschaltet als Trainer oder Leitung',
        zeilen: [`Sieht alle laufenden Zyklen${zyklen.length ? `: ${zyklen.join(', ')}` : ''}.`] };
    case 'pt':    // eigener Plan für PT-Kunden: zugewiesen, Check-Up eingetragen, PT-Buchung bestätigt (Karte darüber)
      return { ok: true, kopf: 'freigeschaltet über den eigenen Plan (PT)',
        zeilen: [`Sieht in der App: ${zyklen.join(', ')}.`] };
    case 'checkup':  // Check-Up-Plan aus der Trainer-App (19.10.2026): freigegeben, für genau diese Person, ohne PT-Zugang
      return { ok: true, kopf: 'freigeschaltet über den Check-Up-Plan',
        zeilen: [`Sieht in der App: ${zyklen.join(', ')}.`] };
    case 'pt_gesperrt':
      return { ok: false, kopf: 'nicht freigeschaltet: Ein eigener Plan ist zugewiesen, aber der PT-Zugang fehlt',
        zeilen: [`Zugewiesen: ${(z.zugewiesene_plaene || []).join(', ')}.`,
          `${z.pt && !z.pt.checkup ? 'Es ist noch kein Check-Up eingetragen.' : 'Die PT-Buchung ist noch nicht bestätigt.'} Beides geht in der Karte „PT-Zugang zum Kraftlog“ oben.`, ...alt] };
    case 'pt_ohne_plan':
      return { ok: false, kopf: 'nicht freigeschaltet: Der PT-Zugang ist bestätigt, aber es läuft kein eigener Plan',
        zeilen: ['Ein Trainer plant einen Fitness-Zyklus, weist ihn der Person zu und gibt ihn frei. Danach steht der Plan in der App im Reiter „Training“.', ...alt] };
    case 'kurs':
      return { ok: true, kopf: `freigeschaltet über ${aufzaehlung(kurse.map((k) => k.kurs_name), 'und')}`,
        // ein Kurs: sein Name steht schon im Kopf; mehrere: je Kurs eine Zeile
        zeilen: [...(kurse.length === 1 ? [`Beleg: ${belegText(kurse[0])}.`] : kurse.map((k) => `${k.kurs_name}: ${belegText(k)}`)),
          `Sieht in der App: ${zyklen.join(', ')}.`,
          ...((z.eigene_plaene || []).length ? [`Darunter der eigene Plan (PT): ${z.eigene_plaene.join(', ')}.`] : [])] };
    case 'kein_vertrag':
      return { ok: false, kopf: 'nicht freigeschaltet, weil keine Mitgliedschaft läuft',
        zeilen: ['Es zählt ein aktiver oder pausierter Vertrag. Probetraining, Urban Sports und Elternkonten von Kindern sind nicht dabei.', ...alt] };
    case 'kein_zyklus':
      if (nachtragen) {
        return { ok: false, kopf: 'kein Zyklus läuft — nur noch Nachtragen',
          zeilen: ['Es läuft kein freigegebener Fitness-Zyklus. Sobald ein Trainer den nächsten freigibt, steht er in der App im Reiter „Training“.'] };
      }
      return { ok: false, kopf: 'zurzeit für niemanden sichtbar',
        zeilen: ['Es läuft kein freigegebener Fitness-Zyklus. Sobald ein Trainer einen freigibt, erscheint der Kraftlog wieder.', ...alt] };
    case 'keine_teilnahme':
      if (!moeglich.length) {
        return { ok: false, kopf: 'nicht freigeschaltet, weil der laufende Zyklus keinem Kurs zugeordnet ist',
          zeilen: ['Einen Zyklus ohne Kurs sehen nur Trainer, Leitung und die Personen, denen er zugewiesen ist (eigener Plan für PT-Kunden).', ...alt] };
      }
      return { ok: false, kopf: 'nicht freigeschaltet, weil das Mitglied in keinem Kurs des Zyklus dabei ist',
        zeilen: [`Dabei ist, wer ${aufzaehlung(moeglich, 'oder')} in der App bucht, dort einen festen Platz hat oder in den letzten ${z.teilnahme_tage || 35} Tagen zur Kurszeit eingecheckt hat. Der Kraftlog steht danach sofort in der App im Reiter „Training“. PT-Kunden bekommen stattdessen einen eigenen Plan: nur nach Check-Up mit PT-Buchung (Karte oben).`, ...alt] };
    default:      // Grund, den diese Fassung noch nicht kennt: wenigstens Ja oder Nein stimmt
      return { ok: z.darf === true, kopf: z.darf === true ? 'freigeschaltet' : 'nicht freigeschaltet', zeilen: [] };
  }
}

export function ZugangZeile({ zugang, hatEintraege = false }) {
  if (!zugang || typeof zugang !== 'object') return null;
  const t = zugangText(zugang, hatEintraege);
  const Icon = t.ok ? Smartphone : Lock;
  return (
    <Card className="border-border/60" data-testid="kraftlog-zugang" data-grund={zugang.grund}>
      <CardContent className="p-3 flex items-start gap-2.5 text-sm">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${t.ok ? 'text-emerald-700' : 'text-amber-700'}`} aria-hidden="true" />
        <div className="min-w-0 space-y-0.5">
          <p className="break-words"><span className="text-muted-foreground">Kraftlog in der App: </span><span className="font-medium">{t.kopf}</span></p>
          {t.zeilen.map((zeile) => <p key={zeile} className="text-xs text-muted-foreground break-words">{zeile}</p>)}
        </div>
      </CardContent>
    </Card>
  );
}

/* Planer-Seite: Wer sieht diesen Zyklus gerade in der App? Lädt erst, wenn die Zeile aufgeklappt ist. */
export function FreigeschaltetListe({ zid, ohneKurs = false }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  useEffect(() => {
    let aktiv = true;
    setDaten(null); setFehler(null);
    trainingslogAPI.zyklus(zid)
      .then((r) => { if (aktiv) setDaten(r.data); })
      .catch((e) => { if (aktiv) setFehler(e?.response?.status === 403 ? `Für diese Liste fehlt dir ${RECHT_FEHLT}.` : 'Die Liste konnte nicht geladen werden.'); });
    return () => { aktiv = false; };
  }, [zid]);

  const kopf = <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kraftlog in der App</p>;
  if (fehler) return <div className="space-y-1" data-testid={`kraftlog-freigeschaltet-${zid}`}>{kopf}<p className="text-xs text-muted-foreground">{fehler}</p></div>;
  if (!daten) return <div className="space-y-1" aria-busy="true" aria-label="Freigeschaltete Mitglieder werden geladen">{kopf}<Skeleton className="h-8" /></div>;
  if (!Array.isArray(daten.freigeschaltet)) return null;

  const mitglieder = daten.freigeschaltet.filter((a) => a.grund !== 'team');
  const team = daten.freigeschaltet.filter((a) => a.grund === 'team');
  const eintraege = Object.fromEntries((daten.athleten || []).map((a) => [a.member_id, a.einheiten]));
  return (
    <div className="space-y-1.5" data-testid={`kraftlog-freigeschaltet-${zid}`}>
      {kopf}
      {!daten.laeuft ? (
        <p className="text-xs text-muted-foreground">Dieser Zyklus läuft heute nicht in der App (nicht freigegeben oder außerhalb seines Zeitraums). Niemand sieht ihn.</p>
      ) : (
        <>
          <p className="text-sm">
            {mitglieder.length === 0 ? 'Noch kein Mitglied ist freigeschaltet.' : `${mitglieder.length} ${mitglieder.length === 1 ? 'Mitglied sieht' : 'Mitglieder sehen'} diesen Zyklus gerade.`}
            <span className="text-muted-foreground">{ohneKurs ? ' Der Zyklus ist keinem Kurs zugeordnet: Ihn sehen Trainer, Leitung und die zugewiesenen Personen mit PT-Zugang.' : ' Dabei ist, wer Mitglied ist und in einem Kurs des Zyklus teilnimmt – oder wem der Plan zugewiesen ist (PT).'}</span>
          </p>
          {mitglieder.length > 0 && (
            <ul className="text-sm divide-y divide-border/40">
              {mitglieder.map((a) => (
                <li key={a.member_id} className="py-1 flex flex-wrap items-baseline gap-x-2">
                  <Link to={`/leads/${a.member_id}`} className="font-medium hover:underline break-words">{a.member_name || 'Ohne Namen'}</Link>
                  <span className="text-xs text-muted-foreground break-words">{a.grund === 'pt' ? 'eigener Plan (PT)' : a.kurse.map((k) => `${k.kurs_name}: ${belegText(k)}`).join(' · ')}</span>
                  {eintraege[a.member_id] ? <Badge variant="secondary" className="ml-auto text-[10px] font-normal">{eintraege[a.member_id]} {eintraege[a.member_id] === 1 ? 'Eintrag' : 'Einträge'}</Badge> : null}
                </li>
              ))}
            </ul>
          )}
          {team.length > 0 && <p className="text-xs text-muted-foreground break-words">Dazu Trainer und Leitung ({team.length}): {team.map((a) => a.member_name || 'Ohne Namen').join(', ')}</p>}
        </>
      )}
    </div>
  );
}

// `speichern` (optional): eigene Ablage, z. B. die Trainer-Webseite /coach; Standard ist das CRM.
export function Kommentar({ eintrag, onGespeichert, speichern: ablage, hinweis = 'Kommentar gespeichert – das Mitglied sieht ihn in der App' }) {
  const [text, setText] = useState(eintrag.coach_kommentar?.text || '');
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const speichern = async () => {
    setBusy(true);
    try {
      const r = await (ablage ? ablage(eintrag.id, text) : trainingslogAPI.kommentar(eintrag.id, text));
      toast.success(text.trim() ? hinweis : 'Kommentar entfernt');
      onGespeichert(r.data); setOffen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kommentar nicht gespeichert'); }
    setBusy(false);
  };
  if (!offen) {
    return (
      <div className="flex items-start gap-2 text-sm">
        {eintrag.coach_kommentar ? (
          <p className="flex-1 rounded-md bg-muted/60 px-3 py-2"><span className="text-xs text-muted-foreground">{eintrag.coach_kommentar.von_name}: </span>{eintrag.coach_kommentar.text}</p>
        ) : <span className="flex-1" />}
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOffen(true)} data-testid={`kraftlog-kommentar-${eintrag.id}`}>
          <MessageSquare className="w-3.5 h-3.5" /> {eintrag.coach_kommentar ? 'Ändern' : 'Kommentieren'}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <label className="sr-only" htmlFor={`kommentar-${eintrag.id}`}>Kommentar an das Mitglied</label>
      <Textarea id={`kommentar-${eintrag.id}`} rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="z. B. Tiefe passt, nächste Woche 2,5 kg mehr" />
      <div className="flex gap-2">
        <Button size="sm" onClick={speichern} disabled={busy}>Speichern</Button>
        <Button size="sm" variant="ghost" onClick={() => { setText(eintrag.coach_kommentar?.text || ''); setOffen(false); }}>Abbrechen</Button>
      </div>
    </div>
  );
}

/* Faden unter dem Coach-Kommentar (13.10.2026, replica/coaching-recherche/fixes.md #1): Das Mitglied antwortet in der App, das
   Team hier (CRM und /coach — EINE Coach-Seite). „Antwort wartet", bis jemand antwortet oder „Gelesen" tippt. */
const amDe = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

export function Faden({ eintrag, onGespeichert, antworten: senden, gelesen: lesen }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const liste = eintrag.antworten || [];
  if (!eintrag.coach_kommentar && !liste.length) return null;
  const tun = async (aufruf, ok) => {
    setBusy(true);
    try { const r = await aufruf(); onGespeichert(r.data); if (ok) { toast.success(ok); setText(''); } }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy(false);
  };
  const antworten = () => tun(() => (senden ? senden(eintrag.id, text) : trainingslogAPI.antwort(eintrag.id, text)), 'Antwort gesendet – das Mitglied sieht sie in der App');
  const gelesen = () => tun(() => (lesen ? lesen(eintrag.id) : trainingslogAPI.gelesen(eintrag.id)));
  return (
    <div className="space-y-2 border-l-2 border-border pl-3" data-testid={`kraftlog-faden-${eintrag.id}`}>
      {liste.map((a) => (
        <p key={a.id} className={`rounded-md px-3 py-2 text-sm break-words ${a.von === 'mitglied' ? 'bg-marke/10' : 'bg-muted/60'}`}>
          <span className="text-xs text-muted-foreground">{a.name || (a.von === 'mitglied' ? 'Mitglied' : 'Coach')}{a.am ? ` · ${amDe(a.am)}` : ''}: </span>{a.text}
        </p>
      ))}
      {eintrag.antwort_offen && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="border-transparent bg-primary text-[10px] font-bold text-primary-foreground hover:bg-primary">Antwort wartet auf euch</Badge>
          <Button size="sm" variant="ghost" disabled={busy} onClick={gelesen} data-testid={`kraftlog-gelesen-${eintrag.id}`}>Gelesen</Button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <label className="sr-only" htmlFor={`antwort-${eintrag.id}`}>Antwort an das Mitglied</label>
        <Textarea id={`antwort-${eintrag.id}`} rows={1} maxLength={500} value={text} onChange={(e) => setText(e.target.value)}
          className="min-h-[44px] text-base sm:text-sm" placeholder={liste.length ? 'Antworten …' : 'Nachfragen oder ergänzen …'} />
        <Button size="sm" className="h-11" disabled={busy || !text.trim()} onClick={antworten} data-testid={`kraftlog-antworten-${eintrag.id}`}>Senden</Button>
      </div>
    </div>
  );
}

export function Block({ z }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-x-3 gap-y-1 py-2 border-t border-border/50 first:border-t-0">
      <div>
        <p className="text-xs font-medium">{z.label}</p>
        <p className="text-[11px] text-muted-foreground">
          {z.slot === 'conditioning'
            ? [z.geplant.format, z.geplant.dauer_min ? `${z.geplant.dauer_min} min` : null].filter(Boolean).join(' · ')
            : sollText(z.geplant)}
        </p>
      </div>
      <div className="min-w-0 text-sm">
        {!z.erledigt && <p className="text-xs text-muted-foreground">nicht eingetragen{z.geplant.uebungen?.length ? ` (${z.geplant.uebungen.join(', ')})` : ''}</p>}
        {z.ist.map((b, i) => (b.ergebnis != null ? (
          <p key={i} className="break-words">{b.ergebnis}</p>
        ) : (
          <div key={i} className="space-y-0.5">
            <p className="font-medium break-words">{b.uebung}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {b.satzzahl} {b.satzzahl === 1 ? 'Satz' : 'Sätze'} · {b.tonnage.toLocaleString('de-DE')} kg bewegt{b.e1rm ? ` · e1RM ${kg(b.e1rm)}` : ''}{b.rpe_schnitt ? ` · Ø RPE ${String(b.rpe_schnitt).replace('.', ',')}` : ''}
              </span>
            </p>
            <p className="text-xs text-muted-foreground break-words">
              {b.saetze.map((s) => `${kg(s.kg)} × ${s.wdh}${s.rpe ? ` @${String(s.rpe).replace('.', ',')}` : ''}`).join('  ·  ')}
            </p>
          </div>
        )))}
      </div>
    </div>
  );
}

export default function TrainingslogTab({ leadId, stand = 0 }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);

  // Die Seite blättert zwischen Leads, ohne neu aufgebaut zu werden: Eine verspätete Antwort zum
  // vorigen Lead darf nicht unter dem neuen stehen — schon gar nicht dessen Zugang.
  const aktuell = useRef(leadId);

  const lade = useCallback(async () => {
    setFehler(null);
    try {
      const d = (await trainingslogAPI.mitglied(leadId)).data;
      if (aktuell.current === leadId) setDaten(d);
    } catch (e) {
      if (aktuell.current !== leadId) return;
      setDaten(null);
      setFehler(e?.response?.status === 403 ? `Für Trainingsdaten fehlt dir ${RECHT_FEHLT}.` : 'Kraftlog konnte nicht geladen werden.');
    }
  }, [leadId]);
  useEffect(() => {
    aktuell.current = leadId;
    setDaten(null);
    lade();
    return () => { aktuell.current = null; };
  }, [leadId, lade]);
  // `stand` zählt die Karte „PT-Zugang“ darüber hoch (Check-Up, bestätigen, beenden): Die Zugangszeile
  // lädt dann still neu — sonst widerspräche sie der Karte, bis jemand den Reiter wechselt.
  const gesehen = useRef(stand);
  useEffect(() => { if (gesehen.current !== stand) { gesehen.current = stand; lade(); } }, [stand, lade]);

  const ersetze = (neu) => setDaten((d) => ({ ...d, eintraege: d.eintraege.map((e) => (e.id === neu.id ? { ...e, ...neu } : e)) }));

  if (fehler) {
    return (
      <Card className="border-border/60"><CardContent className="p-4 text-sm flex items-center justify-between gap-3">
        <span>{fehler}</span><Button size="sm" variant="outline" onClick={lade}>Erneut laden</Button>
      </CardContent></Card>
    );
  }
  if (!daten) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label="Kraftlog wird geladen">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }
  if (!daten.eintraege.length) {
    return (
      <div className="space-y-4">
        <ZugangZeile zugang={daten.zugang} />
        <Card className="border-border/60" data-testid="kraftlog-leer"><CardContent className="p-6 text-center space-y-1">
          <Dumbbell className="w-6 h-6 mx-auto text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">Noch nichts eingetragen</p>
          <p className="text-xs text-muted-foreground">Mitglieder tragen ihre Sätze in der App zur Einheit aus einem freigegebenen Fitness-Zyklus ein. Danach steht hier Plan gegen Ist.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="kraftlog">
      <ZugangZeile zugang={daten.zugang} hatEintraege />
      {daten.bestwerte.length > 0 && (
        <Card className="border-border/60">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4" /> Bestwerte (geschätztes 1RM)</CardTitle></CardHeader>
          <CardContent className="pt-0 flex flex-wrap gap-2">
            {daten.bestwerte.slice(0, 8).map((b) => (
              <Badge key={b.uebung} variant="secondary" className="font-normal max-w-full truncate" title={`${kg(b.top.kg)} × ${b.top.wdh} am ${datumDe(b.datum)}`}>
                {b.uebung}: <span className="ml-1 font-semibold">{kg(b.e1rm)}</span>
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {daten.verlaeufe.filter((v) => v.punkte.length > 1).map((v) => (
        <Card key={v.uebung} className="border-border/60">
          <CardHeader className="pb-2"><CardTitle className="text-sm break-words">Verlauf {v.uebung}</CardTitle></CardHeader>
          <CardContent className="pt-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-muted-foreground text-left"><th className="font-normal py-1">Datum</th><th className="font-normal">Woche</th><th className="font-normal">Bester Satz</th><th className="font-normal">e1RM</th></tr></thead>
              <tbody>
                {v.punkte.map((p) => (
                  <tr key={`${p.zyklus_id}-${p.datum}`} className="border-t border-border/40">
                    <td className="py-1">{datumDe(p.datum)}</td><td>{p.woche}</td><td>{kg(p.top.kg)} × {p.top.wdh}</td>
                    <td className={p.bestwert ? 'font-semibold' : ''}>{kg(p.e1rm)}{p.bestwert ? ' · neu' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {daten.eintraege.map((e) => (
        <Card key={e.id} className="border-border/60" data-testid={`kraftlog-eintrag-${e.id}`}>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{datumDe(e.datum)}</span>
              <span className="text-muted-foreground font-normal break-words">{e.zyklus_name} · Woche {e.woche} · Einheit {e.einheit}</span>
              {!e.coach_kommentar && <Badge variant="outline" className="ml-auto text-[10px]">ohne Kommentar</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {e.plan_gegen_ist
              ? <div>{e.plan_gegen_ist.map((z) => <Block key={z.slot} z={z} />)}</div>
              : (
                <div>
                  <p className="text-xs text-muted-foreground">Den Zyklus zu diesem Eintrag gibt es nicht mehr, deshalb fehlt der Plan.</p>
                  {e.bloecke.map((b) => (
                    <Block key={`${b.slot}-${b.uebung || ''}`} z={{ slot: b.slot, label: b.uebung || b.slot, geplant: {}, erledigt: true,
                      ist: [b.ergebnis != null ? { ergebnis: b.ergebnis } : { ...b, satzzahl: b.saetze.length, tonnage: b.saetze.reduce((t, s) => t + s.kg * s.wdh, 0) }] }} />
                  ))}
                </div>
              )}
            {e.notiz && <p className="text-sm break-words"><span className="text-xs text-muted-foreground">Notiz des Mitglieds: </span>{e.notiz}</p>}
            <Kommentar eintrag={e} onGespeichert={ersetze} />
            <Faden eintrag={e} onGespeichert={ersetze} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
